"use client";

import Mixpanel from "mixpanel-browser";
import config from "@/config";
import {pushGtmEvent} from "@/utils/google-tag-manager";
import {GtmEvents, GtmProperties} from "@/constants/google-tag-manager";
import {getSocialProofTestProps} from "@/utils/ab-tests";
import {upperFirst} from "@/utils";
import {MixpanelEvents, MixpanelProps, MixpanelValues,} from "@/constants/mixpanel";
import {FeatureFlagKeys, FeatureFlagValues} from "@/constants/ab-tests";
import {getSearchParamsObject, setSearchParams} from "@/utils/url";
import {enrichDefaultCampaignData} from "@/utils/campaign";

let mixpanelInitialized = false;

Mixpanel.init(config.mixpanel.token, {
    debug: true,
    loaded: function () {
        mixpanelInitialized = true;
    }
});

const DEV_URL = "https://devweb.atly.com";

export let globalMixpanelData = {};

export const track = (eventName, eventProps) => {
    Mixpanel.track(eventName, {
        ...globalMixpanelData,
        ...eventProps,
    });
};

export const getMixpanelDistinctId = () => {
    return Mixpanel.get_distinct_id();
};

export const identify = (userId) => {
    Mixpanel.identify(userId);
    Mixpanel.people.set("$user_id", userId);
};

export const verifyMixpanelRegistration = () => {
    const registeredProps = {
        distinct_id: Mixpanel.get_property('distinct_id'),
        quiz_flow_variation: Mixpanel.get_property('tt_quiz_flow_variation'),
        ai_flow_variation: Mixpanel.get_property('tt_quiz_ai_flow_variation'),
        checkout_type: Mixpanel.get_property('tt_quiz_checkout_type'),
    };
    console.log('Currently registered Mixpanel properties:', registeredProps);
    return registeredProps;
};

export const registerMixpanelProperties = (data) => {
    const queryParamsObject = getSearchParamsObject();

    const props = {
        ...data,
        [MixpanelProps.MAP_ID]: data.mapId || data.map_id,
        [MixpanelProps.MAP_NAME]: data.mapName || data.map_name,
        [MixpanelProps.TT_NAME]: data.title,
        [MixpanelProps.URL]: window.location.href,
        [MixpanelProps.TEST_VERSION]: "social_proof_A",
        [MixpanelProps.IS_PAID]: data.isPPC ? "Yes" : "No",
        [MixpanelProps.PLATFORM]:
            data.deviceType === "desktop" ? "desktop" : "mobile",
        [MixpanelProps.PAGE_AREA]: data.pageArea,
        [MixpanelProps.TAG]: data.tag_name,
        [MixpanelProps.CITY]: data.city,
        [MixpanelProps.COUNTRY]: data.country,
        [MixpanelProps.REGION]: data.district_or_state,
        [MixpanelProps.LIST_SIZE]: data.stepIdsInPage?.length || 0,
        [MixpanelProps.GLUTEN_FREE_COUNT_IN_AREA]: data.placesCount,
        [MixpanelProps.GLUTEN_FREE_COUNT_IN_AREA_SHOWN]: data.placesCount >= 100,
        [MixpanelProps.USER_SOURCE]: "google_organic_tt",
        [MixpanelProps.USER_AGENT]: window.navigator.userAgent,
        [MixpanelProps.IS_APP_INSTALLED]:
            data?.branchData?.data_parsed && data?.branchData?.data_parsed.has_app
                ? "yes"
                : "no",
        [MixpanelProps.USER_REGION]:
            data?.mapDataRequestLocation?.region_name ||
            data?.mapDataRequestLocation?.city ||
            data?.mapDataRequestLocation?.country,
        ...getSocialProofTestProps({
            count: data.placesCount,
            isMobile: data.isMobile,
            tests: data.tests,
        }),
        ...enrichDefaultCampaignData(queryParamsObject),
    };

    globalMixpanelData = {
        ...globalMixpanelData,
        ...props,
        ...data,
        ...queryParamsObject,
    };

    Mixpanel.register(globalMixpanelData);
};

export const trackIndirectAdsQuizStart = (data = {}) => {
    Mixpanel.track(MixpanelEvents.INDIRECT_ADS_QUIZ_START, {
        ...globalMixpanelData,
        ...data,
    });
};

export const trackStepOnMapClick = (step) => {
    Mixpanel.track(MixpanelEvents.STEP_ON_MAP_CLICK, {
        ...globalMixpanelData,
        [MixpanelProps.STEP_NAME]: step.title,
        [MixpanelProps.STEP_ID]: step._id,
        [MixpanelProps.STEP_RANK_ON_PAGE]: step.index,
        [MixpanelProps.CREATED_BY_MAP_MANAGER]: step.is_created_by_manager,
    });
};

export const trackPinClick = () => {
    Mixpanel.track(MixpanelEvents.TT_PIN_CLICKED, {
        ...globalMixpanelData,
    });
};

export const trackAlsoFeaturedInClick = () => {
    Mixpanel.track(MixpanelEvents.TT_ALSO_FEATURED_IN_CLICK, {
        ...globalMixpanelData,
    });
};

export const trackPopupShowed = () => {
    Mixpanel.track(MixpanelEvents.TT_POPUP_SHOWED, {
        ...globalMixpanelData,
    });
};

export const trackPopupClosed = () => {
    Mixpanel.track(MixpanelEvents.TT_POPUP_CLOSED, {
        ...globalMixpanelData,
    });
};

export const trackSocialProofPopupShown = ({ socialProofVersion, delay }) => {
    Mixpanel.track(MixpanelEvents.TT_SOCIAL_PROOF_POPUP_SHOWN, {
        ...globalMixpanelData,
        [MixpanelProps.SOCIAL_PROOF_TEST_SOCIAL_PROOF_POPUP_VERSION]: "A",
        [MixpanelProps.SOCIAL_PROOF_TEST_SOCIAL_PROOF_POPUP_DELAY]: delay,
    });
};

export const trackStartQuizClick = ({
    area,
    count,
    link,
    isMobile,
    quizFlowControllerVariation,
}) => {
    // document
    //     .querySelector("#drawer-footer-get-the-app-button")
    //     .addEventListener("click", (event) => {
    //         const buttonCta = event.target.textContent;
    //         let randomVariation = localStorage.getItem("gfe-short-cta-target");
    //         if (!randomVariation) {
    //             randomVariation = Math.random() > 0.5 ? "direct_to_app" : "quiz";
    //             localStorage.setItem("gfe-short-cta-target", randomVariation);
    //         }
    //        
    //         Mixpanel.track(MixpanelEvents.TT_START_QUIZ_CLICK, {
    //             [MixpanelProps.GLUTEN_FREE_COUNT_IN_AREA]: count,
    //             ...getSocialProofTestProps({
    //                 buttonCta,
    //                 count,
    //                 isMobile,
    //                 testsData: {
    //                     tests: {
    //                         ctaTarget: randomVariation,
    //                     },
    //                 },
    //             }),
    //         });
    //        
    //         handleRedirect({
    //             shouldRedirect: isMobile,
    //             randomVariation,
    //             link,
    //             area,
    //             quizFlowControllerVariation,
    //         });
    //     });
};

export const trackTopTenStartQuizClick = ({
    count,
    buttonCta,
    isMobile,
    randomVariation,
}) => {
    Mixpanel.track(MixpanelEvents.TT_START_QUIZ_CLICK, {
        ...globalMixpanelData,
        [MixpanelProps.GLUTEN_FREE_COUNT_IN_AREA]: count,
        ...getSocialProofTestProps({
            buttonCta,
            count,
            isMobile,
            testsData: {
                tests: {
                    ctaTarget: randomVariation,
                },
            },
        }),
    });
};
export const getAttributes = (event, elementId) => {
    const targetElement = event.target.closest(elementId);
    const pageTitle = targetElement.getAttribute("data-page-name");
    const position = targetElement.getAttribute("data-position");
    const device = targetElement.getAttribute("data-device");

    return {
        pageTitle,
        position,
        device,
    };
};

export const trackViewMenuButtonClick = (link) => {
    document
        .querySelectorAll("[id^='tt-view-menu-button']")
        .forEach((element) => {
            element.addEventListener("click", (event) => {
                const attributes = getAttributes(event, "[id^='tt-view-menu-button']");
                console.log("MixpanelEvents.TT_VIEW_MENU_BUTTON_CLICK", attributes);

                Mixpanel.track(MixpanelEvents.TT_VIEW_MENU_BUTTON_CLICK, {
                    ...globalMixpanelData,
                });
                pushGtmEvent(GtmEvents.CLICK_INSTALL, {
                    [GtmProperties.GfeTopTen.PAGE_NAME]: attributes.pageTitle,
                    [GtmProperties.GfeTopTen.POSITION]: attributes.position,
                    [GtmProperties.GfeTopTen.ELEMENT_TEXT]: attributes.text,
                    [GtmProperties.GfeTopTen.DEVICE]: attributes.device,
                });

                // window.open(link, "_blank");
            });
        });
};

export const trackReviewsButtonClick = () => {
    document
        .querySelectorAll("[id^='tt-all-reviews-button']")
        .forEach((element) => {
            element.addEventListener("click", (event) => {
                const attributes = getAttributes(
                    event,
                    "[id^='tt-all-reviews-button']"
                );
                console.log("MixpanelEvents.TT_ALL_REVIEWS_BUTTON_CLICK", attributes);

                Mixpanel.track(MixpanelEvents.TT_ALL_REVIEWS_BUTTON_CLICK, {
                    ...globalMixpanelData,
                });
                pushGtmEvent(GtmEvents.CLICK_INSTALL, {
                    [GtmProperties.GfeTopTen.PAGE_NAME]: attributes.pageTitle,
                    [GtmProperties.GfeTopTen.POSITION]: attributes.position,
                    [GtmProperties.GfeTopTen.ELEMENT_TEXT]: attributes.text,
                    [GtmProperties.GfeTopTen.DEVICE]: attributes.device,
                });
            });
        });
};

export const trackFooterAppButtonClick = (link) => {
    document
        .querySelector("#footer-get-the-app-button")
        .addEventListener("click", (event) => {
            const attributes = getAttributes(event, "#footer-get-the-app-button");
            console.log("MixpanelEvents.TT_ALL_REVIEWS_BUTTON_CLICK", attributes);

            trackItemTapped(MixpanelValues.ItemTapped.FOOTER_TOP_BUTTON);

            Mixpanel.track(MixpanelEvents.TT_DOWNLOAD_APP_BUTTON_CLICK, {
                ...globalMixpanelData,
                [MixpanelProps.PLACEMENT]: MixpanelValues.Placement.TOP_OF_FOOTER,
                [MixpanelProps.BUTTON_CTA]: MixpanelValues.ButtonCta.START_FREE_TRIAL,
            });

            pushGtmEvent(GtmEvents.CLICK_INSTALL, {
                [GtmProperties.GfeTopTen.PAGE_NAME]: attributes.pageTitle,
                [GtmProperties.GfeTopTen.POSITION]: "footer",
                [GtmProperties.GfeTopTen.ELEMENT_TEXT]: "start free trial",
                [GtmProperties.GfeTopTen.DEVICE]: attributes.device,
            });

            window.open(link, "_blank");
        });
};

export const trackFooterJoinCommunityButtonClick = (link) => {
    document
        .querySelector("#footer-join-community-button")
        .addEventListener("click", (event) => {
            const attributes = getAttributes(event, "#footer-join-community-button");

            Mixpanel.track(MixpanelEvents.TT_DOWNLOAD_APP_BUTTON_CLICK, {
                ...globalMixpanelData,
                [MixpanelProps.PLACEMENT]: MixpanelValues.Placement.BOTTOM_OF_FOOTER,
                [MixpanelProps.BUTTON_CTA]: MixpanelValues.ButtonCta.JOIN_COMMUNITY,
            });

            trackItemTapped(MixpanelValues.ItemTapped.FOOTER_BOTTOM_BUTTON);

            pushGtmEvent(GtmEvents.CLICK_INSTALL, {
                [GtmProperties.GfeTopTen.PAGE_NAME]: attributes.pageTitle,
                [GtmProperties.GfeTopTen.POSITION]: "footer",
                [GtmProperties.GfeTopTen.ELEMENT_TEXT]: "join the community",
                [GtmProperties.GfeTopTen.DEVICE]: attributes.device,
            });

            window.open(link, "_blank");
        });
};

export const trackPlaceViewed = (placeIndex) => {
    Mixpanel.track(MixpanelEvents.TT_PLACE_VIEWED, {
        ...globalMixpanelData,
        [MixpanelProps.PLACE_IN_LIST]: placeIndex + 1,
    });
};

export const trackPageScrolled = () => {
    Mixpanel.track(MixpanelEvents.TT_PAGE_SCROLLED, {
        ...globalMixpanelData,
    });
};

export const trackItemTapped = (itemName) => {
    console.log('item tapped', { itemName });

    setSearchParams([{ key: "trigger", value: itemName }]);

    Mixpanel.track(MixpanelEvents.TT_ITEM_TAPPED, {
        ...globalMixpanelData,
        [MixpanelProps.TAPPED_ITEM]: itemName,
    });
};

export const trackSliderShown = (trigger) => {
    Mixpanel.track(MixpanelEvents.TT_SLIDER_SHOWN, {
        ...globalMixpanelData,
        [MixpanelProps.TRIGGER]: trigger,
    });
};

export const trackSliderDismissed = (trigger) => {
    Mixpanel.track(MixpanelEvents.TT_SLIDER_DISMISSED, {
        ...globalMixpanelData,
        [MixpanelProps.TRIGGER]: trigger,
    });
};

export const trackSliderAnswerSelected = ({
    trigger,
    answerPosition,
    answerText,
    questionText,
}) => {
    Mixpanel.track(MixpanelEvents.TT_SLIDER_ANSWER_SELECTED, {
        ...globalMixpanelData,
        [MixpanelProps.TRIGGER]: trigger,
        [MixpanelProps.ANSWER_POSITION]: answerPosition,
        [MixpanelProps.ANSWER_TEXT]: answerText,
        [MixpanelProps.QUESTION_TEXT]: questionText,
    });
};

export const trackModalShown = (modalName, buttonCta) => {
    Mixpanel.track(MixpanelEvents.TT_MODAL_SHOWN, {
        ...globalMixpanelData,
        [MixpanelProps.VERSION]: modalName,
        [MixpanelProps.BUTTON_CTA]: upperFirst(buttonCta),
        [MixpanelProps.SOCIAL_PROOF]: "True",
    });
};

export const trackModalCtaClicked = ({
    modalName,
    buttonCta,
    link,
    area,
    quizFlowControllerVariation,
    isMobile,
}) => {
    let randomVariation = localStorage.getItem("gfe-short-cta-target");
    if (!randomVariation) {
        randomVariation = Math.random() > 0.5 ? "direct_to_app" : "quiz";
        localStorage.setItem("gfe-short-cta-target", randomVariation);
    }

    Mixpanel.track(MixpanelEvents.TT_MODAL_CTA_CLICKED, {
        ...globalMixpanelData,
        [MixpanelProps.VERSION]: modalName,
        [MixpanelProps.BUTTON_CTA]: upperFirst(buttonCta),
        [MixpanelProps.SOCIAL_PROOF]: "True",
        [MixpanelProps.BUTTON_DESTINATION]: randomVariation,
    });

    handleRedirect({
        randomVariation,
        link,
        area,
        quizFlowControllerVariation,
        isMobile,
    });
};

export const trackModalDismissed = (modalName, buttonCta) => {
    Mixpanel.track(MixpanelEvents.TT_MODAL_DISMISSED, {
        ...globalMixpanelData,
        [MixpanelProps.VERSION]: modalName,
        [MixpanelProps.BUTTON_CTA]: upperFirst(buttonCta),
        [MixpanelProps.SOCIAL_PROOF]: "True",
    });
};

export const handleRedirect = ({
    randomVariation,
    link,
    area,
    quizFlowControllerVariation,
    shouldRedirect,
    isMobile,
}) => {
    const QUIZ_FLOW_CONTROLLER =
        FeatureFlagValues[FeatureFlagKeys.TT_QUIZ_FLOW_CONTROLLER];
    const VARIATION_A = QUIZ_FLOW_CONTROLLER.A;
    const VARIATION_B = QUIZ_FLOW_CONTROLLER.B;
    const VARIATION_C = QUIZ_FLOW_CONTROLLER.C;

    let path;
    if (isMobile) {
        if (quizFlowControllerVariation === VARIATION_A) {
            path = "gfe-short-checkout";
        } else if (quizFlowControllerVariation === VARIATION_B) {
            path = "gfe-checkout";
        } else if (quizFlowControllerVariation === VARIATION_C && shouldRedirect) {
            // Do not redirect
            return;
        }
    } else {
        let storedPath = localStorage.getItem("gfe-checkout-path");
        if (storedPath) {
            path = storedPath;
        } else {
            path = Math.random() > 0.5 ? "gfe-checkout" : "gfe-short-checkout";
            localStorage.setItem("gfe-checkout-path", path);
        }
    }

    if (path) {
        localStorage.setItem("gfe-short-quiz-area", area);
        const domain = process.env.NEXT_PUBLIC_STAGE
            ? config.top_ten_domain.replace("www", "web")
            : DEV_URL;

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set("lps", "tt");

        window.open(`${domain}/${path}/start?${searchParams.toString()}`, "_blank");
    }
};
